import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { isEmpty } from 'lodash-es'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { createRouteMutation, getAllActiveRoutesQueryKey, getRoutesInAreaQueryKey } from '@/api/main'
import type { DriveType, RouteResponse, RouteSegment, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { DEFAULT_MIN_COVERED_RATIO } from './constants'
import { computePathLengthStats } from './pathStats'
import { canSubmitRoutePlan, routePlanSaveBlockReason } from './saveGate'
import type { PlannerControlSchema } from './schemaSwitch'
import type { PathState, RouteId } from './types'
import { allocateRouteVersion, compareRouteVersionsDesc, isLatestVersion } from './versioning'

const DRIVE_TYPES: DriveType[] = ['statistical', 'evidence', 'documentation', 'testing', 'special']

type RouteSelectorProps = {
  existingRoutes: Array<RouteResponse>
  selectedRoute?: RouteId
  onSelectRoute: (route?: RouteId) => void
  hasPath: boolean
  forceNewRoute?: boolean
  draftRouteName?: string
  onDraftRouteNameChange?: (name: string) => void
}

export const RouteSelector: FC<RouteSelectorProps> = ({
  existingRoutes,
  selectedRoute,
  onSelectRoute,
  hasPath,
  forceNewRoute = false,
  draftRouteName = '',
  onDraftRouteNameChange,
}) => {
  const { t } = useTranslation('routePlanner')

  const uniqueIds = useMemo(() => [...new Set(existingRoutes.map(r => r.id))].sort(), [existingRoutes])

  const versionsForId = useMemo(() => {
    if (!selectedRoute?.id || !uniqueIds.includes(selectedRoute.id)) return []
    return existingRoutes
      .filter(r => r.id === selectedRoute.id)
      .map(r => r.version)
      .sort(compareRouteVersionsDesc)
  }, [existingRoutes, selectedRoute?.id, uniqueIds])

  const isExistingId = !forceNewRoute && selectedRoute?.id ? uniqueIds.includes(selectedRoute.id) : false
  // New-route names are always a local draft: they must never churn the route
  // selection (which resets undo history and can clear the drawn path).
  const nameValue = draftRouteName

  const nameConflict = useMemo(
    () => nameValue.trim().length > 0 && uniqueIds.includes(nameValue.trim()),
    [nameValue, uniqueIds]
  )

  const handleSelectId = (id: string | null) => {
    onDraftRouteNameChange?.('')
    if (!id) {
      onSelectRoute(undefined)
      return
    }
    const newestVersion = existingRoutes
      .filter(r => r.id === id)
      .map(r => r.version)
      .sort(compareRouteVersionsDesc)[0]
    if (newestVersion) onSelectRoute({ id, version: newestVersion })
  }

  const handleSelectVersion = (version: string | null) => {
    if (!version || !selectedRoute?.id) return
    onSelectRoute({ id: selectedRoute.id, version })
  }

  const handleNewName = (name: string) => {
    onDraftRouteNameChange?.(name)
  }

  return (
    <Stack gap="xs">
      {!forceNewRoute && (!hasPath || isExistingId) && (
        <Select
          label={t('route')}
          data={uniqueIds.map(id => ({ value: id, label: id }))}
          value={isExistingId ? selectedRoute!.id : null}
          placeholder={t('selectRoutePlaceholder')}
          onChange={handleSelectId}
          // Re-picking the current route re-frames it on the map; onChange does
          // not fire for same-value submits, so catch those via onOptionSubmit.
          // Keep the current version — re-clicking must not jump to the newest.
          allowDeselect={false}
          onOptionSubmit={id => {
            if (selectedRoute && id === selectedRoute.id) onSelectRoute(selectedRoute)
          }}
          searchable
          clearable
        />
      )}
      {isExistingId && (
        <Select
          label={t('version')}
          data={versionsForId.map(v => ({ value: v, label: v }))}
          value={selectedRoute?.version ?? null}
          onChange={handleSelectVersion}
          allowDeselect={false}
          onOptionSubmit={version => {
            if (selectedRoute && version === selectedRoute.version) onSelectRoute(selectedRoute)
          }}
        />
      )}
      {!isExistingId && (
        <>
          <TextInput
            label={t('newRouteName')}
            placeholder={t('newRouteNamePlaceholder')}
            value={nameValue}
            error={nameConflict ? t('nameAlreadyExists') : undefined}
            onChange={e => handleNewName(e.currentTarget.value)}
          />
          {nameConflict && (
            <Alert color="red" py="xs">
              <Text size="xs">{t('nameConflictAlert', { name: nameValue.trim() })}</Text>
            </Alert>
          )}
        </>
      )}
    </Stack>
  )
}

type PathLengthProps = {
  path: PathState
  segments: Record<string, StreetSegmentPlanningData>
  drivesBySegment: Record<string, { normal: number; reverse: number }>
}

export const PathLength: FC<PathLengthProps> = memo(function PathLength({ path, segments, drivesBySegment }) {
  const { t } = useTranslation('routePlanner')
  const stats = useMemo(
    () => computePathLengthStats(path.segments, segments, drivesBySegment),
    [path.segments, segments, drivesBySegment]
  )

  return (
    <Table>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>{t('totalKmDriven')}</Table.Td>
          <Table.Td ta="right">{stats.driven.toFixed(2)}</Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td>{t('requestedKm')}</Table.Td>
          <Table.Td ta="right">{stats.requested.toFixed(2)}</Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td>{t('billableKm')}</Table.Td>
          <Table.Td ta="right">{stats.billable.toFixed(2)}</Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td>{t('overhead')}</Table.Td>
          <Table.Td ta="right">{stats.overheadPercent.toFixed(2)}%</Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  )
})

type RoutePlannerToolbarProps = {
  path: PathState
  onDeleteLastSegment: () => void
  canUndoLastSegment: boolean
  onUndoLastOperation: () => void
  canUndoOperation: boolean
  onSelectRoute: (routeId: RouteId | undefined, opts?: { forceSync?: boolean }) => void
  onRouteSaved?: (segments: RouteSegment[], route: RouteId) => void
  onCancelChanges: (() => void) | null
  selectedRoute?: RouteId
  existingRoutes: Array<RouteResponse>
  areaId: string
  displayAllNodes: boolean
  setDisplayAllNodes: (value: boolean) => void
  displayOffNetworkNodes: boolean
  setDisplayOffNetworkNodes: (value: boolean) => void
  displaySignPosts: boolean
  setDisplaySignPosts: (value: boolean) => void
  displayParkingAreas: boolean
  setDisplayParkingAreas: (value: boolean) => void
  displayNodeNumbers: boolean
  setDisplayNodeNumbers: (value: boolean) => void
  controlDataSchema: PlannerControlSchema
  onControlDataSchemaChange: (schema: PlannerControlSchema) => void
  routeExists: boolean
  routeLoadError?: boolean
  onRequestSaveConfirm: (save: () => void) => void
  createMode: boolean
  hasActiveEdit: boolean
  hasBlockingGaps: boolean
}

export const RoutePlannerToolbar: FC<RoutePlannerToolbarProps> = ({
  path,
  onDeleteLastSegment,
  canUndoLastSegment,
  onUndoLastOperation,
  canUndoOperation,
  onCancelChanges,
  existingRoutes,
  selectedRoute,
  onSelectRoute,
  onRouteSaved,
  areaId,
  displayAllNodes,
  setDisplayAllNodes,
  displayOffNetworkNodes,
  setDisplayOffNetworkNodes,
  displaySignPosts,
  setDisplaySignPosts,
  displayParkingAreas,
  setDisplayParkingAreas,
  displayNodeNumbers,
  setDisplayNodeNumbers,
  controlDataSchema,
  onControlDataSchemaChange,
  routeExists,
  routeLoadError = false,
  onRequestSaveConfirm,
  createMode,
  hasActiveEdit,
  hasBlockingGaps,
}) => {
  const { t } = useTranslation(['routePlanner', 'scheduling'])
  const queryClient = useQueryClient()
  const [draftRouteName, setDraftRouteName] = useState('')
  const pendingSaveSegmentsRef = useRef<RouteSegment[] | null>(null)
  const form = useForm({
    initialValues: { driveType: ['statistical'] as DriveType[] },
    validate: {
      driveType: (value: DriveType[]) => (isEmpty(value) ? t('noDriveTypeSelected') : null),
    },
  })

  const uniqueRouteIds = useMemo(() => new Set(existingRoutes.map(r => r.id)), [existingRoutes])

  useEffect(() => {
    setDraftRouteName('')
  }, [createMode])

  const draftRouteId = draftRouteName.trim()
  const draftNameConflict = draftRouteId.length > 0 && uniqueRouteIds.has(draftRouteId)
  const draftReady = draftRouteId.length > 0 && !draftNameConflict
  const saveTargetId = createMode
    ? draftReady
      ? draftRouteId
      : undefined
    : (selectedRoute?.id ?? (draftReady ? draftRouteId : undefined))
  const saveBlockReason = routePlanSaveBlockReason({ hasActiveEdit, hasBlockingGaps })
  const canSubmit = canSubmitRoutePlan({
    hasSaveTarget: Boolean(saveTargetId),
    routeLoadError,
    hasActiveEdit,
    hasBlockingGaps,
  })

  const driveTypeData = useMemo(
    () =>
      DRIVE_TYPES.map(value => ({
        value,
        label: t(`driveTypes.${value}`, { ns: 'scheduling', defaultValue: value }),
      })),
    [t]
  )

  useEffect(() => {
    const driveTypes = existingRoutes.find(
      ({ id, version }) => id === selectedRoute?.id && version === selectedRoute?.version
    )?.allowed_drive_types
    form.setValues({ driveType: driveTypes ? [...driveTypes] : ['statistical'] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute?.id, selectedRoute?.version, existingRoutes])

  const { mutate, isPending } = useMutation({
    ...createRouteMutation(),
    onSuccess: response => {
      notifications.show({
        message: t('routeSaved', { id: response.id, version: response.version }),
        color: 'green',
      })
      void queryClient.invalidateQueries({ queryKey: getRoutesInAreaQueryKey({ path: { area_id: areaId } }) })
      void queryClient.invalidateQueries({ queryKey: getAllActiveRoutesQueryKey() })
      const route = { id: response.id, version: response.version }
      const savedSegments = pendingSaveSegmentsRef.current ?? path.segments
      pendingSaveSegmentsRef.current = null
      queryClient.setQueryData(['routePlanner', 'getRoute', route.id, route.version], {
        ...response,
        route_plan: savedSegments,
        district_boundaries_wkt: '',
      })
      onSelectRoute(route, { forceSync: true })
      onRouteSaved?.(savedSegments, route)
    },
    onError: () => {
      pendingSaveSegmentsRef.current = null
      notifications.show({ message: t('saveFailed'), color: 'red' })
    },
  })

  const versionsForSelected = useMemo(
    () => (selectedRoute ? existingRoutes.filter(r => r.id === selectedRoute.id).map(r => r.version) : []),
    [existingRoutes, selectedRoute]
  )

  const editingExisting = routeExists && !createMode
  const editingNonLatest =
    editingExisting && selectedRoute ? !isLatestVersion(selectedRoute.version, versionsForSelected) : false

  const performSave = (driveType: DriveType[]) => {
    if (!saveTargetId) return
    const routeVersion = allocateRouteVersion(createMode ? [] : versionsForSelected)
    pendingSaveSegmentsRef.current = path.segments
    mutate({
      path: { route_id: saveTargetId, route_version: routeVersion },
      body: {
        route_plan: path.segments,
        planning_area_id: areaId,
        allowed_drive_types: driveType,
        min_covered_ratio: DEFAULT_MIN_COVERED_RATIO,
      },
    })
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>{t('title')}</Text>
        <Button component={Link} to="/routes" variant="subtle" size="xs">
          {t('backToRoutes')}
        </Button>
      </Group>

      {(editingExisting || editingNonLatest) && (
        <Alert color="orange" title={t('savingCreatesNewVersionTitle')}>
          {editingNonLatest ? t('editingNonLatestMessage') : t('editingExistingMessage')}
        </Alert>
      )}

      {createMode && (
        <Alert color="blue" title={t('createModeBannerTitle')}>
          {t('createModeBannerMessage')}
        </Alert>
      )}

      <form
        onSubmit={form.onSubmit(values => {
          if (!canSubmit || !saveTargetId || !values.driveType) return
          const run = () => performSave(values.driveType)
          if (editingExisting) {
            onRequestSaveConfirm(run)
          } else {
            run()
          }
        })}
      >
        <RouteSelector
          existingRoutes={existingRoutes}
          selectedRoute={selectedRoute}
          onSelectRoute={onSelectRoute}
          hasPath={path.segments.length > 0}
          forceNewRoute={createMode}
          draftRouteName={draftRouteName}
          onDraftRouteNameChange={setDraftRouteName}
        />

        <Switch
          mt="md"
          label={t('displayAllNodes')}
          checked={displayAllNodes}
          onChange={event => setDisplayAllNodes(event.currentTarget.checked)}
        />

        <Switch
          mt="xs"
          label={t('displayOffNetworkNodes')}
          checked={displayOffNetworkNodes}
          onChange={event => setDisplayOffNetworkNodes(event.currentTarget.checked)}
        />

        <Switch
          mt="xs"
          label={t('displaySignPosts')}
          checked={displaySignPosts}
          onChange={event => setDisplaySignPosts(event.currentTarget.checked)}
        />

        <Switch
          mt="xs"
          label={t('displayParkingAreas')}
          checked={displayParkingAreas}
          onChange={event => setDisplayParkingAreas(event.currentTarget.checked)}
        />

        <Switch
          mt="xs"
          label={t('displayNodeNumbers')}
          checked={displayNodeNumbers}
          onChange={event => setDisplayNodeNumbers(event.currentTarget.checked)}
        />

        <Select
          mt="md"
          label={t('mapData')}
          data={[
            { value: 'vehicle', label: t('schemaProduction') },
            { value: 'next', label: t('schemaWorking') },
          ]}
          value={controlDataSchema}
          onChange={value => value && onControlDataSchemaChange(value as PlannerControlSchema)}
          allowDeselect={false}
        />

        {(selectedRoute || draftReady) && (
          <MultiSelect label={t('driveType')} data={driveTypeData} mt="sm" {...form.getInputProps('driveType')} />
        )}

        {saveBlockReason && (
          <Alert color="orange" py="xs" mt="sm">
            <Text size="xs">
              {saveBlockReason === 'activeEdit' ? t('saveBlockedEditActive') : t('saveBlockedGaps')}
            </Text>
          </Alert>
        )}

        <Button loading={isPending} type="submit" mt="md" disabled={!canSubmit} fullWidth>
          {editingExisting ? t('saveAsNewVersion') : t('createAndSave')}
        </Button>

        <Group mt="md" gap="xs">
          <Tooltip label={t('undoTooltip')}>
            <Button onClick={onDeleteLastSegment} disabled={!canUndoLastSegment} variant="outline" size="xs">
              {t('undo')}
            </Button>
          </Tooltip>
          <Tooltip label={t('undoOperationTooltip')}>
            <Button onClick={onUndoLastOperation} disabled={!canUndoOperation} variant="outline" size="xs">
              {t('undoOperation')}
            </Button>
          </Tooltip>
          {onCancelChanges && (
            <Button onClick={onCancelChanges} variant="outline" color="red" size="xs">
              {t('cancelChanges')}
            </Button>
          )}
        </Group>
      </form>
    </Stack>
  )
}
